import Image from "next/image"
import { useAmp } from "next/amp"
import { Fragment, useEffect } from "react"
import classnames from "classnames"
import styled from "styled-components"
import { useStore } from "@features/common"
import { AmpImage } from "@lib/amp"
import { RichContentView } from "@ui"
import { dateFormater } from "@lib/date"
import { observer } from "mobx-react"
import { RouterLink } from "@lib/routing"
import { useShowSubscriptionDialog } from "@features/subscription"
import * as Sentry from "@sentry/nextjs"

import type { PostElementBackground, PostModel } from "@serializers/article"

type ArticleProps = {
  article: PostModel
}

export const Article = observer(function Article(props: ArticleProps) {
  const { article } = props
  const {
    BlogModel,
    ResponsiveModel: { isMobile, isHighResolution, isWebPSupported },
  } = useStore()
  const isAmp = useAmp()

  useShowSubscriptionDialog()

  const { image_cover } = article
  const coverDesktop = image_cover.formats.article_cover_desktop || image_cover
  const coverMobile = image_cover.formats.article_cover_mobile || image_cover
  const cover1x = isMobile ? coverMobile : coverDesktop
  const cover2x = isMobile
    ? image_cover.formats.article_cover_mobile_x2
    : image_cover.formats.article_cover_desktop_x2

  const coverDefault = isHighResolution ? cover2x ?? cover1x : cover1x
  const coverFallback =
    image_cover.formats.article_cover_original ?? image_cover
  const cover = isWebPSupported ? coverDefault : coverFallback
  const coverAlt = image_cover.alternativeText || article.title
  const coverTitle = image_cover.caption

  useEffect(() => {
    if (!isWebPSupported && cover.url.endsWith(".webp")) {
      Sentry.captureMessage(
        `Article #${article.id} (${article.slug}, ${article.lang}) cover: WebP image isn't supported`,
      )
    }
  }, [isWebPSupported, cover.url, article.id, article.slug, article.lang])

  return (
    <article
      className="article w-full flex flex-col justify-start"
      data-test-id="article"
    >
      <div>
        <div className="container mt-7 tablet-m:mt-9">
          <div className="flex justify-between items-center">
            <div>
              {article.category ? (
                <RouterLink
                  uri={`/journal/categories/${article.category.slug}`}
                  className={classnames(
                    "block px-2.5 pt-[9px] pb-[5px]",
                    "bg-white border border-black",
                    "font-sans-bold text-sm uppercase tracking-0.5",
                    "transition-colors duration-150 ease-in hover:bg-gold-200",
                  )}
                  data-test-id="article__category_button"
                >
                  {article.category.name}
                </RouterLink>
              ) : null}
            </div>
            {article.published_at ? (
              <time className="text-sm text-gray-250 tablet:text-base">
                {dateFormater(
                  new Date(article.published_at),
                  "dd MMMM yyyy",
                  article.lang,
                )}
              </time>
            ) : (
              <div className="p-2 w-auto inline-flex text-lg text-white bg-gold-400 rounded-lg">
                {BlogModel.getTemplate("notPublished")}
              </div>
            )}
          </div>
          <h1 className="font-serif text-2xl uppercase tracking-1 my-6 tablet-m:my-8 tablet:text-4xl tablet:tracking-2 desktop:text-5xl">
            {article.title}
          </h1>
        </div>
        {cover.url ? (
          <div className="select-none container">
            <Fragment>
              {isAmp ? (
                <AmpImage
                  width={String((cover.width / cover.height).toFixed(2))}
                  src={cover.url}
                  alt={coverAlt}
                  title={coverTitle}
                />
              ) : (
                <Image
                  src={cover.url}
                  width={cover.width}
                  height={cover.height}
                  layout="responsive"
                  alt={coverAlt}
                  title={coverTitle}
                  priority={true}
                  unoptimized={true}
                />
              )}
            </Fragment>
          </div>
        ) : null}
        <div className="container py-6 tablet-m:py-8 space-y-8">
          <RichContentView
            title={`${article.title}.description`}
            src={article.description}
            className="max-w-full tablet:max-w-[750px]"
          />
        </div>
      </div>

      {article.elements.map((element, index) => {
        const length = article.elements.length || 1
        const prevColor: string =
          index === 0 || length === 1
            ? "white"
            : article.elements![index - 1].background
        return (
          <BlogElement
            key={index}
            title={`${article.title}.${index}`}
            background={element.background}
            content={element.content}
            topMargin={element.background === "white" && prevColor !== "white"}
            bottomPadding={index !== length - 1}
          />
        )
      })}
    </article>
  )
})

interface BlogElementProps {
  background: PostElementBackground
  bottomPadding: boolean
  title: string
  content: string
  topMargin: boolean
}

export function BlogElement(props: BlogElementProps) {
  const { title, content, background, topMargin, bottomPadding } = props

  const backgroundBlockColor = (color: string) => {
    switch (color) {
      case "black":
        return "black"
      case "white":
        return "white"
      case "gray":
        return "#F5F3F3"
      default:
        return "black"
    }
  }

  const textBlockColor = (backgroundColor: string) => {
    switch (backgroundColor) {
      case "black":
        return "white"
      default:
        return "black"
    }
  }

  if (background === "white") {
    return (
      <div
        className={classnames({
          "mt-6 tablet-m:mt-8": topMargin,
          "pb-6 tablet-m:pb-8": bottomPadding,
          "pb-0": !bottomPadding,
        })}
      >
        <div className="container">
          <BlogElementRichContent src={content} title={title} />
        </div>
      </div>
    )
  }

  return (
    <div
      className={classnames("pt-6 tablet-m:pt-8", {
        "pb-6 tablet-m:pb-8": bottomPadding,
        "pb-0": !bottomPadding,
      })}
      style={{ backgroundColor: backgroundBlockColor(background) }}
    >
      <div className="container">
        <BlogElementRichContent
          src={content}
          title={title}
          color={textBlockColor(background)}
        />
      </div>
    </div>
  )
}

const BlogElementRichContent = styled(RichContentView)<{ color?: string }>`
  color: ${(props) => props.color};
  max-width: 750px;

  & blockquote {
    & > p {
      padding-bottom: 8px;
    }
    &:before {
      background: ${(props) => props.color};
    }
  }

  ${(props) => props.theme.ViewPorts.mobile} {
    max-width: 100%;
  }
`
